import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  Subject,
  Observable,
  exhaustMap,
  forkJoin,
  map,
  of,
  tap,
  catchError,
  lastValueFrom,
  from,
  switchMap,
} from 'rxjs';
import { fromPromise } from 'rxjs/internal/observable/innerFrom';
import { LocalStorage } from 'src/app/shared/providers/local-storage/local-storage';
import { NetworkProvider } from 'src/app/shared/providers/network/network';
import { CrtData } from '../../models/data';
import { Lr, TypeLR } from '../../models/lr';
import { LrRaw } from '../../models/lrRaw';
import { CrtLrReglageCollection } from '../../models/reglage-lr';
import { CrtHttpErrorHandlerService } from '../../providers/http-error-handler.service';
import { CrtLrMapperService } from './lr-mapper.service';
import { CrtDocumentsService, TypeImage } from './documents.service';
import { Storage } from '@ionic/storage';
import { CrtDataService } from './data.service';
import { CrtReglageLrService } from './reglage-lr.service';
import { servicesUrl } from '../../config';

/**
 * Service pour la stockage local des loi de régulation
 */
@Injectable({
  providedIn: 'root',
})
export class LrService extends LocalStorage {
  /**
   * Observable sur la mise à jour d'une LR
   */
  public updateLr$: Subject<Lr>;
  /**
   * clef de stockage des lois de régulation pour un site
   * @type {string}
   */
  private readonly DATAS_STORAGE_KEY = 'crt-${idSite}-lois-regulation';

  /**
   * Constructeur
   * @param {Storage} storage
   * @param {NetworkProvider} network
   * @param {HttpClient} http
   * @param {CrtHttpErrorHandlerService} crtHttpErrorHandler
   * @param {CrtLrMapperService} crtLrMapperService
   * @param crtDataService
   */
  constructor(
    protected override storage: Storage,
    protected override network: NetworkProvider,
    protected http: HttpClient,
    private crtHttpErrorHandler: CrtHttpErrorHandlerService,
    private crtLrMapperService: CrtLrMapperService,
    private crtDataService: CrtDataService,
    private crtReglageLrService: CrtReglageLrService,
    private crtDocumentService: CrtDocumentsService
  ) {
    super();
    this.serverRequestPeriod = 10;
    this.updateLr$ = new Subject<Lr>();
  }

  /**
   * Recupère les lois de régulation d'un site
   * @param {number} siteId
   * @param {boolean} forceRefresh
   * @returns {Observable<Lr[]>}
   */
  public getLoisRegulationListByIdSite(idSite: number, forceRefresh = false): Observable<Lr[]> {
    const key = this.formatStorageKey(this.DATAS_STORAGE_KEY, { idSite });
    const url = servicesUrl.getLoiRegulationBySite({ idSite });

    return this.shouldUseWebservice(key, forceRefresh).pipe(
      exhaustMap((shouldUseWebservice) => {
        if (shouldUseWebservice) {
          return this.crtReglageLrService.getAllLrReglagesByIdSite(idSite).pipe(
            exhaustMap((reglagesLr) => {
              return this.http.get<LrRaw[]>(url).pipe(
                exhaustMap((loisRegulation) => {
                  if (loisRegulation.length) {
                    return forkJoin(
                      loisRegulation.map((lr) =>
                        this.getSimpleDataFromLrByIdSite(lr, idSite, forceRefresh).pipe(
                          map((crtDatas) => {
                            return this.crtLrMapperService.mapLrRawToLr(lr, crtDatas);
                          })
                        )
                      )
                    );
                  } else {
                    return of([]);
                  }
                }),
                map((loisRegulation: Lr[]) => this.crtLrMapperService.mapStatusToLrList(loisRegulation, reglagesLr)),
                tap(async (loisRegulation: Lr[]) => {
                  await this.storage.set(key, loisRegulation);
                  await this.setLastRequestDateToNow(key);
                }),
                tap(async (loisRegulation: Lr[]) => {
                  let imgArrayObs = [];
                  loisRegulation.forEach(async (lr) => {
                    if (lr.typeLR === TypeLR.LOI_GENERIQUE_CONSIGNE) {
                      imgArrayObs.push(
                        await lastValueFrom(
                          this.crtDocumentService.getDocuments(
                            lr.id,
                            TypeImage.IMAGE_LOI,
                            lr.image,
                            lr.transmetteur,
                            true
                          )
                        )
                      );
                    }
                  });
                  await Promise.all(imgArrayObs);
                })
              );
            }),
            catchError(async (e) => {
              return from(this.crtHttpErrorHandler.handleError(e)).pipe(
                switchMap(() => fromPromise(this.storage.get(key)))
              );
            })
          );
        } else {
          return fromPromise(this.storage.get(key));
        }
      })
    );
  }

  private async fetchLrsConsignesImages(loisRegulation: Lr[]) {
    let imgArrayObs = [];

    loisRegulation.forEach(async (lr) => {
      if (lr.typeLR !== TypeLR.LOI_GENERIQUE_CONSIGNE) return;

      imgArrayObs.push(
        lastValueFrom(this.crtDocumentService.getDocuments(lr.id, TypeImage.IMAGE_LOI, lr.image, lr.transmetteur, true))
      );
    });

    await Promise.all(imgArrayObs);
  }

  private mapLrRawToLrAndInjectCrtDatas(
    loisRegulation: LrRaw[],
    idSite: number,
    forceRefresh: boolean,
    reglagesLr: CrtLrReglageCollection
  ) {
    return forkJoin(
      loisRegulation.map((lr) =>
        this.getSimpleDataFromLrByIdSite(lr, idSite, forceRefresh).pipe(
          map((crtDatas) => {
            return this.crtLrMapperService.mapStatusToLr(
              this.crtLrMapperService.mapLrRawToLr(lr, crtDatas),
              reglagesLr
            );
          })
        )
      )
    );
  }

  /**
   * récupérer les lrs en local par tableau d'id
   * @param {number} idSite
   * @param {number} phId
   * @returns {Observable<Ph>}
   */
  public getLrsFromStorageByIdSite(idSite: number, lrIds: number[]): Observable<Lr[]> {
    const key = this.formatStorageKey(this.DATAS_STORAGE_KEY, { idSite });
    return fromPromise(this.storage.get(key)).pipe(
      map((lrListe) => {
        return lrListe.filter((lr) => {
          if (lrIds) {
            return lrIds.includes(lr.id);
          }
          return null;
        });
      }),
      map(this.orderByPrioAffAndDesc)
    );
  }

  /**
   * Tri le tableau des LRsq par priorite puis par descritpion
   * @param listeLr
   */
  private orderByPrioAffAndDesc(listeLr: Lr[]): Lr[] {
    listeLr.sort((objet1, objet2) => {
      if (objet1.priorite === objet2.priorite) {
        return objet1.descriptionMetier < objet2.descriptionMetier ? -1 : 1;
      } else {
        return objet1.priorite - objet2.priorite;
      }
    });
    return listeLr;
  }

  /**
   * Récupere une LR via son ID
   * @param idSite
   * @param lrId
   * @param forceRefresh
   */
  public getLoiRegultationByIdWithIdSite(idSite: number, lrId: number, forceRefresh = false): Observable<Lr> {
    return this.getLoisRegulationListByIdSite(idSite, forceRefresh).pipe(
      map((lrs: Lr[]) => {
        let lr: Lr = lrs.find((lr) => lr.id === lrId);
        this.updateLr$.next(lr);
        return lr;
      })
    );
  }

  /**
   * Met à jour les statuts d'envoi des LR en localstorage
   * Notifie les souscriptions au sujet updateLr$
   * @param idSite
   * @param lrIds identifiants des lr à notifier
   * @param reglagesLr
   */
  public updateLoisRegulationStatusWithIdSite(
    idSite: number,
    lrIds: number[],
    reglagesLr: CrtLrReglageCollection,
    forceRefresh = false
  ): Observable<Lr[]> {
    const key = this.formatStorageKey(this.DATAS_STORAGE_KEY, { idSite });

    return this.getLoisRegulationListByIdSite(idSite, forceRefresh).pipe(
      map((loisRegulation: Lr[]) => this.crtLrMapperService.mapStatusToLrList(loisRegulation, reglagesLr)),
      tap(async (loisRegulation: Lr[]) => {
        await this.storage.set(key, loisRegulation);

        lrIds.forEach((lrId) => {
          let lr: Lr = loisRegulation.find((lr) => lr.id === lrId);
          this.updateLr$.next(lr);
        });
      })
    );
  }

  /**
   * Récupération de toutes les données simple d'une LR
   * @param lr
   * @param siteId Identifiant du site
   * @param forceRefresh Forcer l'actualisation
   */
  public getSimpleDataFromLrByIdSite(lr: LrRaw, idSite: number, forceRefresh = false): Observable<CrtData[]> {
    return this.crtDataService.getDataByIdsAndIdSite(
      idSite,
      lr.loiRegulationDTO.donneesSimples.map((data) => data.id),
      forceRefresh
    );
  }
}
